import { useEffect, useRef, useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import { AnchoredPopover } from '../shared/AnchoredPopover';
import { STATUS_ACTIONS, REASON_CODES, statusLabel } from './salesStatusVocab';
import { describeError } from './salesPlanErrors';
import type { SalesCloseOutStatus, SalesViewClient } from '../../api/types';

const POP_CLASS = 'sv-status-menu';

interface SalesStatusControlProps {
  client: Pick<
    SalesViewClient,
    | 'client_id'
    | 'user_status'
    | 'user_status_by'
    | 'user_status_at'
    | 'user_status_reason_text'
    | 'user_status_follow_up_date'
    | 'user_status_expired'
  >;
  onStatusChange: (clientId: string, status: SalesCloseOutStatus, updatedAt: string | null) => void;
  /** `card` is the compact form; `panel` is the popout footer. */
  variant?: 'card' | 'panel';
}

/**
 * Set a close-out status (§7.4). Rendered on BOTH the card and the popout: a rep
 * triaging a list should not have to open a panel to dismiss, and one who has just
 * read the detail should not have to close it to act.
 *
 * The menu is an `AnchoredPopover` and MUST stay one. The carousel and dashboard
 * both set `overflow: hidden`, which clips an in-flow absolute popover regardless
 * of z-index — with no console error, and jsdom does not clip, so the test suite
 * stays green while the menu is invisible.
 */
export function SalesStatusControl({
  client,
  onStatusChange,
  variant = 'card',
}: SalesStatusControlProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [followUp, setFollowUp] = useState('');

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      // Portalled: the menu is not a DOM descendant of its anchor, so without
      // this its own clicks read as "outside" and close it instantly.
      if (t?.closest?.(`.${POP_CLASS}`)) return;
      if (wrapRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const act = async (status: SalesCloseOutStatus) => {
    setSaving(true);
    setError(null);
    try {
      const res = await siftApiClient.setSalesViewClientStatus(client.client_id, {
        status,
        reason_code: reasonCode || undefined,
        reason_text: reasonText.trim() || undefined,
        follow_up_date: status === 'follow_up' && followUp ? followUp : undefined,
      });
      onStatusChange(client.client_id, status, res.updated_at ?? null);
      setOpen(false);
      setReasonCode('');
      setReasonText('');
      setFollowUp('');
    } catch (err) {
      console.error('Close-out failed:', err);
      // The backend names which stage failed and whether retrying is worth it; a flat
      // "Could not save" hid that, so an outage read the same as a rejected value.
      setError(describeError(err, 'Could not save. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  const current = client.user_status;
  const by = client.user_status_by;

  return (
    <div
      className={`sv-status-control sv-status-control-${variant}`}
      ref={wrapRef}
      // The card is itself clickable and opens the popout.
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={`sv-status-btn${current ? ' sv-status-btn-set' : ''}${
          client.user_status_expired ? ' sv-status-btn-due' : ''
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {current ? statusLabel(current) : 'Set status'}
        {client.user_status_expired && <span className="sv-status-due-dot" title="Due again" />}
        <span className="sv-status-caret">▾</span>
      </button>

      {current && by && (
        <span className="sv-status-by" title={`Set by ${by}`}>
          {/* Statuses are per TEAM, so this may not be the person reading it.
              Without a name an overwrite by a colleague or manager is silent. */}
          {by.split('@')[0]}
        </span>
      )}

      {open && (
        <AnchoredPopover
          anchorRef={wrapRef}
          align="left"
          className={POP_CLASS}
          role="menu"
          ariaLabel="Set client status"
          style={{
            minWidth: 260,
            background: 'var(--color-surface, #fff)',
            border: '1px solid rgba(0,0,0,0.15)',
            borderRadius: 8,
            boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
            padding: 8,
          }}
        >
          <div className="sv-status-menu-list">
            {STATUS_ACTIONS.map((a) => (
              <button
                key={a.value}
                type="button"
                role="menuitem"
                className={`sv-status-menu-item${current === a.value ? ' active' : ''}`}
                title={a.hint}
                disabled={saving}
                onClick={() => act(a.value)}
              >
                <span className="sv-status-menu-label">{a.label}</span>
                <span className="sv-status-menu-hint">{a.hint}</span>
              </button>
            ))}
          </div>

          <div className="sv-status-menu-optional">
            <label className="sv-status-field">
              <span>Reason</span>
              <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
                {REASON_CODES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </label>

            <label className="sv-status-field">
              <span>Follow-up date</span>
              <input
                type="date"
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
              />
            </label>

            <label className="sv-status-field sv-status-field-wide">
              <span>Note</span>
              <textarea
                rows={2}
                maxLength={2000}
                placeholder="Optional"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
              />
            </label>
            <p className="sv-status-menu-note">
              Both are optional — pick a status and move on if that is all you need.
            </p>
          </div>

          {error && <div className="sv-closeout-error">{error}</div>}
        </AnchoredPopover>
      )}
    </div>
  );
}

export default SalesStatusControl;
